import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FiArrowRight, FiLoader } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'
import MarkdownContent from '../../components/blog/shortcodes'
import { formatearFecha } from '../../utils/fechas'

// Página dinámica de detalle de post. Hace fetch a /api/blog/posts/:slug y
// monta BlogLayout (con post normalizado) + MarkdownContent (renderiza Markdown
// + shortcodes del cuerpo). Si el slug no existe en BD, muestra un 404 SEO-safe.
//
// Esta página convive con las rutas hardcoded de los 11 posts JSX existentes.
// React Router 6 prefiere matches estáticos (/blog/sanciones-no-fichar-2026)
// sobre dinámicos (/blog/:slug), así que los posts viejos siguen funcionando
// hasta que se migren en Fase 2E. Esta ruta solo capturará slugs nuevos.

const normalizar = (apiPost) => ({
  title: apiPost.titulo,
  description: apiPost.descripcion,
  excerpt: apiPost.excerpt,
  date: apiPost.fecha_publicacion,
  dateDisplay: formatearFecha(apiPost.fecha_publicacion),
  category: apiPost.categoria_nombre || apiPost.categoria?.nombre || '',
  readMinutes: apiPost.read_minutes,
})

const normalizarRelacionados = (rels) =>
  (rels || []).map((r) => ({
    slug: r.slug,
    title: r.titulo,
    excerpt: r.excerpt,
    category: r.categoria?.nombre || '',
  }))

export default function BlogPost() {
  const { slug } = useParams()
  const [estado, setEstado] = useState('cargando') // cargando | encontrado | no_encontrado | error
  const [post, setPost] = useState(null)
  const [relacionados, setRelacionados] = useState([])

  useEffect(() => {
    let cancelado = false
    setEstado('cargando')

    fetch(`/api/blog/posts/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (res.status === 404) return { _notFound: true }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (cancelado) return
        if (data._notFound) { setEstado('no_encontrado'); return }
        setPost(data.post)
        setEstado('encontrado')

        // Relacionados: pedimos otros posts publicados de la misma categoría.
        // Es un fetch separado y best-effort: si falla, no rompemos el render.
        const cat = data.post?.categoria_slug || data.post?.categoria?.slug
        if (cat) {
          fetch(`/api/blog/posts?categoria=${encodeURIComponent(cat)}&page_size=4`)
            .then((r) => r.ok ? r.json() : null)
            .then((d) => {
              if (cancelado || !d) return
              const otros = (d.posts || []).filter((p) => p.slug !== slug).slice(0, 3)
              setRelacionados(otros)
            })
            .catch(() => {})
        }
      })
      .catch(() => { if (!cancelado) setEstado('error') })

    return () => { cancelado = true }
  }, [slug])

  if (estado === 'cargando') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center text-gray-400">
          <FiLoader className="h-7 w-7 mx-auto animate-spin" />
          <p className="mt-3 text-sm">Cargando artículo…</p>
        </div>
      </div>
    )
  }

  if (estado === 'no_encontrado') {
    return (
      <>
        <Helmet>
          <title>Artículo no encontrado | PresenzIA</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-3">Artículo no encontrado</h1>
            <p className="text-gray-600 mb-8 leading-relaxed">
              El enlace que has seguido no apunta a ningún artículo del blog. Puede que el contenido se haya movido o eliminado.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl text-sm"
            >
              Volver al blog <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </>
    )
  }

  if (estado === 'error' || !post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No hemos podido cargar el artículo</h1>
          <p className="text-gray-600 mb-6 text-sm">Vuelve a intentarlo en unos segundos. Si persiste, escríbenos.</p>
          <Link to="/blog" className="text-blue-600 hover:text-blue-700 font-semibold text-sm">← Volver al blog</Link>
        </div>
      </div>
    )
  }

  const postLayout = normalizar(post)
  const toc = Array.isArray(post.toc) ? post.toc : []
  const faqs = Array.isArray(post.faq) ? post.faq.map((f) => ({ question: f.question, answer: f.answer })) : []

  return (
    <BlogLayout
      slug={slug}
      post={postLayout}
      toc={toc}
      faqs={faqs}
      related={normalizarRelacionados(relacionados)}
    >
      <MarkdownContent contenido={post.contenido_md} />
    </BlogLayout>
  )
}
